import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Pill, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { cn } from '@/shared/cn';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { BASIS_LABEL } from '@/versions/v1/shared/comparison-basis';
import { DashboardPanel } from './RoleLandingPanels';
import type { ByAreaRow, DeviationStatus } from './Performance.transform';

const COMPACT_CELL = 'px-1 py-1 text-[11px]';
const NUMERIC_CELL = 'px-0.5 py-1 text-[11px]';

function statusPill(status: DeviationStatus): { label: string; tone: 'success' | 'warning' | 'danger' | 'default' } {
  switch (status) {
    case 'ok':
      return { label: 'On/above', tone: 'success' };
    case 'watch':
      return { label: 'Watch', tone: 'warning' };
    case 'below':
      return { label: 'Below', tone: 'danger' };
    default:
      return { label: 'Gap', tone: 'default' };
  }
}

function ByAreaRowCells({ row }: { row: ByAreaRow }) {
  const [open, setOpen] = useState(false);
  const pill = statusPill(row.status);
  const hasDetail = Boolean(row.designDetail);

  return (
    <>
      <TR>
        <TD className={cn('font-medium text-zinc-900 dark:text-zinc-100', COMPACT_CELL)}>
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              disabled={!hasDetail}
              onClick={() => setOpen((v) => !v)}
              className={cn(
                'inline-flex w-4 shrink-0 items-center justify-center',
                !hasDetail && 'pointer-events-none',
              )}
              aria-expanded={hasDetail ? open : undefined}
              aria-label={hasDetail ? (open ? 'Hide design detail' : 'Show design detail') : undefined}
            >
              {hasDetail ? (
                open ? (
                  <ChevronDown className="h-3 w-3" strokeWidth={2} />
                ) : (
                  <ChevronRight className="h-3 w-3" strokeWidth={2} />
                )
              ) : (
                <ChevronRight className="h-3 w-3 opacity-0" aria-hidden />
              )}
            </button>
            <span>{row.area}</span>
          </span>
        </TD>
        <TD className={COMPACT_CELL}>{row.variable}</TD>
        <TD className={cn(NUMERIC_CELL, 'whitespace-nowrap tabular-nums')}>{row.dailyCell}</TD>
        <TD className={cn(NUMERIC_CELL, 'whitespace-nowrap tabular-nums')}>{row.weeklyCell}</TD>
        <TD className={cn(NUMERIC_CELL, 'whitespace-nowrap tabular-nums')}>{row.mtdCell}</TD>
        <TD className={cn(COMPACT_CELL, 'whitespace-nowrap')}>
          <Pill tone={pill.tone}>{pill.label}</Pill>
        </TD>
      </TR>
      {hasDetail && open ? (
        <TR>
          <TD colSpan={6} className="bg-[var(--surface-2)] px-2 py-1.5 text-xs text-muted">
            {row.designDetail}
          </TD>
        </TR>
      ) : null}
    </>
  );
}

export function PerformanceByAreaTable({
  rows,
  weeklyProvenance,
  mtdProvenance,
  basisLabel,
}: {
  rows: readonly ByAreaRow[];
  weeklyProvenance?: string;
  mtdProvenance?: string;
  /** `ComparisonBasisRead.label` shared by every row (pl.D1 round 2, #142)
   * — read from the caller, never hardcoded on the column headers below. */
  basisLabel?: string;
}) {
  const panelProvenance =
    weeklyProvenance && mtdProvenance && weeklyProvenance === mtdProvenance
      ? weeklyProvenance
      : [weeklyProvenance, mtdProvenance].filter(Boolean).join(' · ');
  const basisWord = basisLabel ?? BASIS_LABEL.budget;

  return (
    <DashboardPanel
      title="Performance by process area — daily / weekly / MTD"
      meta={
        panelProvenance ? (
          <span className="inline-flex shrink-0">
            <ProvenanceChip text={panelProvenance} />
          </span>
        ) : undefined
      }
    >
      <Table className="table-fixed">
        <colgroup>
          <col className="w-[24%]" />
          <col className="w-[13%]" />
          <col className="w-[14%]" />
          <col className="w-[13%]" />
          <col className="w-[13%]" />
          <col className="w-[18%]" />
        </colgroup>
        <THead>
          <TR>
            <TH className={COMPACT_CELL}>Area</TH>
            <TH className={COMPACT_CELL}>Variable</TH>
            <TH className={cn(NUMERIC_CELL, 'whitespace-nowrap tabular-nums')}>Daily vs {basisWord}</TH>
            <TH className={cn(NUMERIC_CELL, 'whitespace-nowrap tabular-nums')}>Wk vs {basisWord}</TH>
            <TH className={cn(NUMERIC_CELL, 'whitespace-nowrap tabular-nums')}>MTD vs {basisWord}</TH>
            <TH className={COMPACT_CELL}>Status</TH>
          </TR>
        </THead>
        <TBody>
          {rows.map((row) => (
            <ByAreaRowCells key={row.id} row={row} />
          ))}
        </TBody>
      </Table>
    </DashboardPanel>
  );
}
